import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { ExternalLink, Disc3, Music2 } from "lucide-react";

function formatReleaseDate(iso) {
  if (!iso) return "TBA";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" }).toUpperCase();
}

export default function Releases() {
  const kicker = useContent("page.releases.kicker", "/ 07 — RELEASES");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const title = useContent("page.releases.title", "TRACKS & LABELS");
  const intro = useContent(
    "page.releases.intro",
    "Original productions, VA contributions and remixes. Released works will appear here as they drop."
  );
  const emptyMsg = useContent(
    "page.releases.empty_state",
    "Productions in the pipeline. This space will fill up with tracks, VAs and label releases — stay tuned."
  );

  useEffect(() => {
    api
      .get("/releases")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="releases-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16" data-testid="releases-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {title}
          </h1>
          <p className="font-body text-silver-400 text-sm md:text-base max-w-xl mt-8 leading-relaxed">
            {intro}
          </p>
        </div>

        {loading && (
          <p className="font-body text-silver-500 text-sm" data-testid="releases-loading">
            Loading…
          </p>
        )}

        {!loading && items.length === 0 && (
          <div className="hairline-t pt-16 flex flex-col items-center gap-6 text-center" data-testid="releases-empty">
            <Disc3 size={36} className="text-silver-700 animate-pulse-slow" />
            <div>
              <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-400 mb-3">
                / COMING SOON
              </div>
              <p className="font-body text-silver-500 text-sm max-w-md">
                {emptyMsg}
              </p>
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-silver-800" data-testid="releases-list">
            {items.map((r) => (
              <ReleaseCard key={r.id} r={r} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReleaseCard({ r }) {
  return (
    <article
      className="group bg-black flex flex-col hover:bg-silver-900/40 transition-all duration-500"
      data-testid={`release-${r.id}`}
    >
      <div className="aspect-square bg-silver-900 hairline-b overflow-hidden relative">
        {r.image_url ? (
          <img
            src={r.image_url}
            alt={r.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            style={{ filter: "grayscale(15%) contrast(1.05)" }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music2 size={48} className="text-silver-700" />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className="font-title text-[0.55rem] tracking-[0.24em] text-silver-100 bg-black/70 border border-silver-600 px-2 py-1 uppercase backdrop-blur">
            {r.kind}
          </span>
        </div>
      </div>
      <div className="p-6 flex flex-col gap-3 flex-1">
        <div className="font-title text-[0.6rem] tracking-[0.3em] text-silver-500">
          {formatReleaseDate(r.release_date)}
          {r.label && <> · {r.label}</>}
        </div>
        <h3 className="font-title text-lg tracking-[0.08em] text-silver-100 leading-snug">
          {r.title}
        </h3>
        {r.notes && (
          <p className="font-body text-xs text-silver-500 leading-relaxed line-clamp-3">
            {r.notes}
          </p>
        )}
        {r.link_url && (
          <a
            href={r.link_url}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost mt-auto pt-4 self-start"
            data-testid={`release-link-${r.id}`}
          >
            LISTEN <ExternalLink size={12} />
          </a>
        )}
      </div>
    </article>
  );
}
